import { excelEuroFmt, excelNumFmt, excelPercentFmt } from '~/utils/excel/exportColumns'

export const TONNAGE_DECIMALS = {
  totals: 2,
  categories: 2,
  chart: 2,
  chartSmall: 3,
  dailySheet: 3,
} as const

export const UVC_DECIMALS = 0
export const EURO_DECIMALS = 0
export const SHARE_DECIMALS = 0

type NumberStyle = 'decimal' | 'percent'

const FORMATTERS = new Map<string, Intl.NumberFormat>()

const formatterFor = (style: NumberStyle, decimals: number): Intl.NumberFormat => {
  const cacheKey = `${style}:${decimals}`
  const cached = FORMATTERS.get(cacheKey)
  if (cached !== undefined) return cached
  const created = new Intl.NumberFormat('fr-FR', {
    style,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  FORMATTERS.set(cacheKey, created)
  return created
}

export const frNumberFormat = (decimals: number): Intl.NumberFormat =>
  formatterFor('decimal', decimals)

const countLabel = (count: number, noun: string): string =>
  `${frNumberFormat(0).format(count)} ${noun}${count > 1 ? 's' : ''}`

export const scopeCountLabel = (
  storeCount: number,
  projectCount: number,
  nouns: { store: string; project: string } = { store: 'Boutique', project: 'Projet' },
): string => {
  const parts: string[] = []
  if (storeCount > 0 || projectCount === 0) parts.push(countLabel(storeCount, nouns.store))
  if (projectCount > 0) parts.push(countLabel(projectCount, nouns.project))
  return parts.join(' · ')
}

/**
 * CA prévu de la page : au prix encaissé (point prévisions du 22/10/2026), calculé par le backend
 * (`realizedPrice`) ; les prévisions à 5 ans apprennent déjà sur le CA encaissé.
 */
export const PAID_CA_LABEL = 'CA encaissé'

export const PAID_CA_HINT =
  'CA au prix encaissé : anti-gaspi, CE et 1 acheté = 1 offert déduits. Vues jour et semaine : prix catalogue × rapport CA payé / CA catalogue des 28 derniers jours, par boutique et catégorie (et par jour de la semaine en vue jour). Vue mois : prévisions des boutiques apprises sur le CA encaissé. Projets : rapport du réseau sur toute la semaine.'

export const frPercentFormat = (decimals: number): Intl.NumberFormat =>
  formatterFor('percent', decimals)

export const EXCEL_NUM_FMT = {
  uvc: excelNumFmt(UVC_DECIMALS),
  euro: excelEuroFmt(EURO_DECIMALS),
  share: excelPercentFmt(SHARE_DECIMALS),
  date: 'dd/mm/yyyy',
} as const

export const frenchShortDate = (iso: string): string => {
  const [year, month, day] = iso.slice(0, 10).split('-')
  return year && month && day ? `${day}/${month}/${year}` : iso
}

/** Noms de catégorie des vues projection et du filtre de catégories, en capitales. */
export const upperCategoryName = (name: string): string => name.toLocaleUpperCase('fr-FR')
